import Cookies from 'js-cookie';
import { DEFAULT_REDIRECT_URL } from 'dashboard/constants/globals';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { readLastModuleId } from 'dashboard/helper/aceleresLastModule';

export const hasAuthCookie = () => {
  return !!Cookies.get('cw_d_session_info');
};

const getSSOAccountPath = ({ ssoAccountId, user }) => {
  const { accounts = [], account_id = null } = user || {};
  const ssoAccount = accounts.find(
    account => account.id === Number(ssoAccountId)
  );
  let accountPath = '';
  if (ssoAccount) {
    accountPath = `accounts/${ssoAccountId}`;
  } else if (accounts.length) {
    // If the account id is not found, redirect to the first account
    const accountId = account_id || accounts[0].id;
    accountPath = `accounts/${accountId}`;
  }
  return accountPath;
};

const capitalize = str =>
  str
    .split(/[._-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const getCredentialsFromEmail = email => {
  const [localPart, domain] = email.split('@');
  const namePart = localPart.split('+')[0];
  return {
    fullName: capitalize(namePart),
    accountName: capitalize(domain.split('.')[0]),
  };
};

/**
 * O Aceleres SEMPRE anexa um `sso_redirect_path` ao link de SSO, mesmo quando o
 * usuário só clicou no botão "Zoraxy": veja ChatwootService::generateSsoUrl, que
 * usa `/app/accounts/<id>/conversations` como valor padrão quando nenhum destino
 * foi pedido. Portanto "veio um sso_redirect_path" NÃO significa "o usuário quis
 * ir para um lugar específico" — só um caminho diferente desse padrão significa
 * isso (o /funil, o assistente do Instagram, o deep link de uma conversa).
 * Sem esta distinção o destino fixo ganha de tudo e o último módulo nunca vale.
 */
const isDefaultLandingPath = (pathname, accountPath) => {
  if (!accountPath) return false;

  return [
    frontendURL(`${accountPath}/conversations`),
    frontendURL(`${accountPath}/dashboard`),
  ].includes(pathname);
};

export const getLoginRedirectURL = ({
  ssoAccountId,
  ssoConversationId,
  ssoRedirectPath,
  user,
}) => {
  const accountPath = getSSOAccountPath({ ssoAccountId, user });
  const rememberedModule = readLastModuleId({ userId: user?.id });

  if (ssoRedirectPath) {
    try {
      const redirectURL = new URL(ssoRedirectPath, window.location.origin);
      const isInternalAppPath =
        redirectURL.origin === window.location.origin &&
        (redirectURL.pathname === '/funil' ||
          redirectURL.pathname === '/app' ||
          redirectURL.pathname.startsWith('/app/')) &&
        ssoRedirectPath.startsWith('/') &&
        !ssoRedirectPath.startsWith('//');

      const isJustTheDefaultLanding =
        rememberedModule &&
        isDefaultLandingPath(redirectURL.pathname, accountPath) &&
        !redirectURL.search &&
        !redirectURL.hash;

      if (isInternalAppPath && !isJustTheDefaultLanding) {
        return `${redirectURL.pathname}${redirectURL.search}${redirectURL.hash}`;
      }
    } catch (error) {
      // Ignore malformed or external SSO redirect paths and use the safe default.
    }
  }

  if (accountPath) {
    if (ssoConversationId) {
      return frontendURL(`${accountPath}/conversations/${ssoConversationId}`);
    }
    // Aceleres: entrar no Zoraxy é sempre um SSO vindo do Aceleres, então este
    // é o caminho por onde o usuário reabre o sistema — não só depois de um
    // logout. Se ele tem um módulo guardado (o logout apaga), devolvemos a URL
    // da conta SEM rota: o router do dashboard cai no galho de "URL sem rota" e
    // resolve o módulo lá, com a tabela de rotas de verdade. Assim o caminho
    // não é duplicado aqui.
    if (rememberedModule) {
      return frontendURL(accountPath);
    }
    return frontendURL(`${accountPath}/dashboard`);
  }
  return DEFAULT_REDIRECT_URL;
};
